import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Header from "../../components/content_wrapper/header";
import Pagination from "../../utils/pagination";
import {
  useHostBillingStatement,
  useHostBillingCycles,
  useHostBillingSettings,
  useUpdateHostBillingSettings,
  useHostBillingRates,
  useCreateHostBillingRate,
  useUpdateHostBillingRate,
  useDeleteHostBillingRate,
  useHostBillingOverrides,
  useUpsertHostBillingOverride,
  useDeleteHostBillingOverride,
  exportBillingSummaryCsv,
  exportBillingStatementCsv,
} from "../../hooks/hostBilling";
import { useGetKeyHost } from "../../hooks/keyHostManagementApi/getKeyHost";

// ─── helpers ─────────────────────────────────────────────────────────

const FREQUENCIES = ["weekly", "biweekly", "monthly", "quarterly"] as const;
const COUNTABLE_OPS = ["guest_collect", "guest_return", "owner_collect"];

const CURRENCY_SYMBOL: Record<string, string> = { GBP: "£", USD: "$", EUR: "€" };
const money = (pence: number | undefined, currency = "GBP") => {
  const sym = CURRENCY_SYMBOL[currency] || `${currency} `;
  return `${sym}${((pence || 0) / 100).toFixed(2)}`;
};
const round1 = (n: number | undefined) =>
  n == null ? "0" : (Math.round(Number(n) * 10) / 10).toString();

// "guest_collect" → "guest collect"
const humanizeOp = (op: string) => op.replace(/_/g, " ");
const humanizeOps = (ops: string[] | undefined) =>
  (ops || []).map(humanizeOp).join(", ") || "—";
const exchangeTip = (ops: string[] | undefined) =>
  `Each ${humanizeOps(ops)} counts as 1 exchange — a guest round-trip (collect then return) is 2.`;
// the API's by_operation keys come back camelCased ("guestCollect"); short column
// label: drop a leading "guest" and title-case → "Collect" / "Return" / "Owner collect"
const opShort = (camel: string) => {
  const w = camel.replace(/([A-Z])/g, " $1").toLowerCase().trim().replace(/^guest /, "");
  return w.charAt(0).toUpperCase() + w.slice(1);
};

const fmtDay = (sec: number | undefined | null) =>
  sec == null ? "—" : moment.unix(Number(sec)).format("MMM DD, YYYY");
const fmtDateTime = (sec: number | undefined | null) =>
  sec == null ? "—" : moment.unix(Number(sec)).format("MMM DD, YYYY HH:mm");

/** a day (YYYY-MM-DD) → unix seconds at noon UTC, so the API's cycleContaining
 *  snaps it to the right cycle regardless of timezone. */
const dayToCycleHint = (ymd: string) =>
  Math.floor(new Date(`${ymd}T12:00:00Z`).getTime() / 1000);
const todayYmd = () => moment().format("YYYY-MM-DD");
const secToYmd = (sec: number | undefined | null) =>
  sec == null ? "" : moment.unix(Number(sec)).format("YYYY-MM-DD");
const nowSec = () => Math.floor(Date.now() / 1000);

const apiErr = (e: any, fallback: string) =>
  e?.displayMessage || e?.userMessage || e?.response?.data?.message || fallback;

// void-returning toast wrappers — react-query mutation callbacks must return void
const okToast = (msg: string): void => {
  toast.success(msg);
};
const errToast = (e: any, fallback: string): void => {
  toast.error(apiErr(e, fallback));
};

// ─── rate editor (one dimension for one host) ────────────────────────

const RateEditor: React.FC<{
  keyHostId: string;
  dimension: "key_days" | "exchanges";
  rows: any[];
}> = ({ keyHostId, dimension, rows }) => {
  const create = useCreateHostBillingRate();
  const update = useUpdateHostBillingRate();
  const del = useDeleteHostBillingRate();

  const [newPounds, setNewPounds] = useState("");
  const [newFrom, setNewFrom] = useState(todayYmd());
  const [editId, setEditId] = useState<string | null>(null);
  const [editPounds, setEditPounds] = useState("");
  const [editFrom, setEditFrom] = useState("");

  const label = dimension === "key_days" ? "Key-day rate" : "Exchange rate";
  const unit = dimension === "key_days" ? "per key per day" : "per counted exchange";

  const poundsToPence = (v: string) => Math.round(parseFloat(v) * 100);

  const add = () => {
    const pence = poundsToPence(newPounds);
    if (!Number.isInteger(pence) || pence < 0) {
      toast.error("Enter a rate like 0.12");
      return;
    }
    create.mutate(
      { keyHostId, dimension, ratePence: pence, effectiveFrom: dayToCycleHint(newFrom) },
      {
        onSuccess: () => {
          toast.success(`${label} added`);
          setNewPounds("");
        },
        onError: (e: any) => errToast(e, "Could not add rate"),
      }
    );
  };

  const saveEdit = (id: string) => {
    const pence = poundsToPence(editPounds);
    if (!Number.isInteger(pence) || pence < 0) {
      toast.error("Enter a rate like 0.12");
      return;
    }
    update.mutate(
      { id, ratePence: pence, effectiveFrom: dayToCycleHint(editFrom) },
      {
        onSuccess: () => {
          toast.success("Rate updated — past cycles will re-compute");
          setEditId(null);
        },
        onError: (e: any) => errToast(e, "Could not update rate"),
      }
    );
  };

  const remove = (id: string) => {
    Swal.fire({
      title: "Delete this rate row?",
      text: "Cycles that fell under it will re-compute with the next earlier rate (or £0 if none).",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    }).then((r) => {
      if (!r.isConfirmed) return;
      del.mutate(
        { id },
        {
          onSuccess: () => okToast("Rate deleted"),
          onError: (e: any) => errToast(e, "Could not delete rate"),
        }
      );
    });
  };

  return (
    <div className="mb-3">
      <div className="fw-semibold mb-1">
        {label} <span className="text-muted small">({unit})</span>
      </div>
      <table className="display border-0 w-100 mb-2">
        <thead>
          <tr>
            <th>Effective from</th>
            <th>Rate</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} className="text-muted small">
                No rate set — this dimension bills £0 until a rate is added.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r._id}>
              {editId === r._id ? (
                <>
                  <td>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={editFrom}
                      onChange={(e) => setEditFrom(e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-control form-control-sm"
                      value={editPounds}
                      onChange={(e) => setEditPounds(e.target.value)}
                    />
                  </td>
                  <td className="text-nowrap">
                    <button
                      className="btn btn-sm btn-primary me-1"
                      disabled={update.isLoading}
                      onClick={() => saveEdit(r._id)}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setEditId(null)}
                    >
                      Cancel
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td>{fmtDay(r.effectiveFrom)}</td>
                  <td>{money(r.ratePence)}</td>
                  <td className="text-nowrap">
                    <button
                      className="btn btn-sm btn-outline-primary me-1"
                      onClick={() => {
                        setEditId(r._id);
                        setEditPounds(((r.ratePence || 0) / 100).toFixed(2));
                        setEditFrom(secToYmd(r.effectiveFrom));
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      disabled={del.isLoading}
                      onClick={() => remove(r._id)}
                    >
                      Delete
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="d-flex gap-2 align-items-end flex-wrap">
        <div>
          <label className="form-label small text-muted mb-0">Effective from</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={newFrom}
            onChange={(e) => setNewFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="form-label small text-muted mb-0">Rate (e.g. 0.12)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            className="form-control form-control-sm"
            value={newPounds}
            onChange={(e) => setNewPounds(e.target.value)}
          />
        </div>
        <button
          className="btn btn-sm btn-primary"
          disabled={create.isLoading}
          onClick={add}
        >
          Add rate
        </button>
      </div>
    </div>
  );
};

// ─── per-host cycle override ─────────────────────────────────────────

const OverrideEditor: React.FC<{ keyHostId: string; override: any }> = ({
  keyHostId,
  override,
}) => {
  const upsert = useUpsertHostBillingOverride();
  const del = useDeleteHostBillingOverride();

  const [anchor, setAnchor] = useState<string>(override?.cycleAnchor ?? "");
  const [freq, setFreq] = useState<string>(override?.cycleFrequency ?? "");
  const [anchorDate, setAnchorDate] = useState<string>(secToYmd(override?.anchorDate));
  const [billingStart, setBillingStart] = useState<string>(
    secToYmd(override?.billingStartDate)
  );

  const save = () => {
    upsert.mutate(
      {
        keyHostId,
        cycleAnchor: anchor === "" ? null : anchor,
        cycleFrequency: (freq === "" ? null : freq) as any,
        anchorDate: anchorDate === "" ? null : dayToCycleHint(anchorDate),
        billingStartDate: billingStart === "" ? null : dayToCycleHint(billingStart),
      },
      {
        onSuccess: () => okToast("Override saved — past cycles will re-compute"),
        onError: (e: any) => errToast(e, "Could not save override"),
      }
    );
  };

  const clear = () => {
    del.mutate(
      { keyHostId },
      {
        onSuccess: () => {
          toast.success("Override removed — host falls back to global settings");
          setAnchor("");
          setFreq("");
          setAnchorDate("");
          setBillingStart("");
        },
        onError: (e: any) => errToast(e, "Could not remove override"),
      }
    );
  };

  return (
    <div className="row g-2 align-items-end">
      <div className="col-sm-3">
        <label className="form-label small text-muted mb-0">
          Billing start (bill nothing before)
        </label>
        <input
          type="date"
          className="form-control form-control-sm"
          value={billingStart}
          onChange={(e) => setBillingStart(e.target.value)}
        />
      </div>
      <div className="col-sm-3">
        <label className="form-label small text-muted mb-0">Cycle anchor (1–28 or "last")</label>
        <input
          className="form-control form-control-sm"
          value={anchor}
          placeholder="(global)"
          onChange={(e) => setAnchor(e.target.value.trim())}
        />
      </div>
      <div className="col-sm-3">
        <label className="form-label small text-muted mb-0">Cycle frequency</label>
        <select
          className="form-select form-select-sm"
          value={freq}
          onChange={(e) => setFreq(e.target.value)}
        >
          <option value="">(global)</option>
          {FREQUENCIES.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </div>
      <div className="col-sm-3">
        <label className="form-label small text-muted mb-0">
          Anchor date (weekly/biweekly)
        </label>
        <input
          type="date"
          className="form-control form-control-sm"
          value={anchorDate}
          onChange={(e) => setAnchorDate(e.target.value)}
        />
      </div>
      <div className="col-12 mt-2">
        <button
          className="btn btn-sm btn-primary me-2"
          disabled={upsert.isLoading}
          onClick={save}
        >
          Save override
        </button>
        {override && (
          <button
            className="btn btn-sm btn-outline-danger"
            disabled={del.isLoading}
            onClick={clear}
          >
            Remove override
          </button>
        )}
      </div>
    </div>
  );
};

// ─── one cycle's statement (read-only) ──────────────────────────────

// key name (falls back to a short id) + owner email, for line-item rows
const keyNameCell = (row: any) => row?.keyName || `key …${String(row?.keyId || "").slice(-6)}`;

const METRIC_LABEL: Record<string, string> = {
  key_days: "days",
  exchanges: "exchanges",
};

const StatementView: React.FC<{
  keyHostId: string;
  cycleStart: number;
  onGoToSettings: () => void;
}> = ({ keyHostId, cycleStart, onGoToSettings }) => {
  const { data, isLoading, isFetching } = useHostBillingStatement(keyHostId, cycleStart);
  const setMetric = useUpsertHostBillingOverride();
  const [csvBusy, setCsvBusy] = useState(false);

  const st = data?.data;
  const currency = st?.currency || "GBP";
  const inProgress = st
    ? st.inProgress ?? Number(st.cycleEnd) > nowSec()
    : false;

  const downloadCsv = () => {
    setCsvBusy(true);
    exportBillingStatementCsv(keyHostId, cycleStart)
      .catch((e) => toast.error(apiErr(e, "CSV export failed")))
      .finally(() => setCsvBusy(false));
  };

  const chooseMetric = (m: "key_days" | "exchanges") =>
    setMetric.mutate(
      { keyHostId, billingMetric: m },
      {
        onSuccess: () => okToast(`Billing this host on ${METRIC_LABEL[m]}`),
        onError: (e: any) => errToast(e, "Could not set the billing metric"),
      }
    );

  if (isLoading) return <p className="text-muted">Computing…</p>;
  if (!st) return <p className="text-danger">Could not load this statement.</p>;

  const kdRate = !!st.keyDays?.rateConfigured;
  const exRate = !!st.exchanges?.rateConfigured;
  const billMetric: string | null = st.billableMetric ?? null;

  const header = (
    <div className="d-flex justify-content-between align-items-start mb-2">
      <div className="text-muted small">
        Cycle {st.cycleStartLabel || fmtDay(st.cycleStart)} –{" "}
        {st.cycleEndLabel || fmtDay(st.cycleEnd)}
        {st.timezone ? ` (${st.timezone})` : ""}
        {inProgress && (
          <span className="badge bg-info text-dark ms-2">in progress</span>
        )}
        {st.partialFirstCycle && (
          <span
            className="badge bg-warning text-dark ms-2"
            title={`Billing window starts ${st.windowStartLabel || fmtDay(st.windowStart)}`}
          >
            partial — bills from {st.windowStartLabel || fmtDay(st.windowStart)}
          </span>
        )}
      </div>
      <button
        className="btn btn-sm btn-outline-secondary text-nowrap"
        disabled={csvBusy || !billMetric}
        title={billMetric ? "" : "Set a billing metric first"}
        onClick={downloadCsv}
      >
        <i className="fa-solid fa-download me-1" />
        CSV
      </button>
    </div>
  );

  // No rate at all → nothing to bill.
  if (!kdRate && !exRate) {
    return (
      <>
        {header}
        <div className="alert alert-warning">
          <p className="mb-2">
            No billing rate is configured for this host, so there's no statement
            to produce.
          </p>
          <button className="btn btn-sm btn-primary" onClick={onGoToSettings}>
            Add a rate on the Settings tab
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {header}

      {inProgress && (
        <div className="alert alert-info py-2 small mb-3">
          This cycle is still running. Days count up to now and keep rising
          until it closes — treat the total as provisional.
        </div>
      )}

      {st.metricChoiceRequired ? (
        <div className="alert alert-warning">
          <p className="mb-2 fw-semibold">
            This host has rates on <em>both</em> metrics. Pick the one to bill on
            — the other stays configured but unused.
          </p>
          <button
            className="btn btn-sm btn-outline-primary me-2"
            disabled={setMetric.isLoading}
            onClick={() => chooseMetric("key_days")}
          >
            Bill on days ({round1(st.keyDays?.total)} → {money(st.keyDays?.amountPence, currency)})
          </button>
          <button
            className="btn btn-sm btn-outline-primary"
            disabled={setMetric.isLoading}
            onClick={() => chooseMetric("exchanges")}
            title={exchangeTip(st.countedOperations)}
          >
            Bill on exchanges ({st.exchanges?.count ?? 0} → {money(st.exchanges?.amountPence, currency)})
          </button>
        </div>
      ) : (
        <div className="wrapper-card mb-3 text-center border-primary" style={{ borderWidth: 2 }}>
          <div className="text-muted small">Amount to bill</div>
          <div className="fs-3 fw-bold">{money(st.billableAmountPence, currency)}</div>
          <div className="text-muted small">
            billed on {METRIC_LABEL[billMetric || ""] || billMetric} ·{" "}
            {billMetric === "key_days"
              ? `${round1(st.keyDays?.total)} days`
              : `${st.exchanges?.count ?? 0} exchanges`}
          </div>
        </div>
      )}

      {/* ── billed metric, in detail ── */}
      {billMetric === "key_days" ? (
        <>
          <div className="fw-semibold mb-1">Days breakdown</div>
          <div className="table-responsive mb-2">
            <table className="display border-0 w-100">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Owner</th>
                  <th className="text-end">Days</th>
                  <th className="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(st.keyDays?.lineItems || []).length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-muted small">
                      No billable days in this cycle.
                    </td>
                  </tr>
                )}
                {(st.keyDays?.lineItems || []).map((li: any) => (
                  <tr key={String(li.keyId)}>
                    <td>{keyNameCell(li)}</td>
                    <td className="small text-muted">{li.ownerEmail || "—"}</td>
                    <td className="text-end">{round1(li.keyDays)}</td>
                    <td className="text-end">{money(li.amountPence, currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="fw-semibold">
                  <td colSpan={2}>Total</td>
                  <td className="text-end">{round1(st.keyDays?.total)}</td>
                  <td className="text-end">{money(st.keyDays?.amountPence, currency)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : (
        (() => {
          const opCols: string[] = Object.keys(st.exchanges?.byOperation || {});
          const items: any[] = st.exchanges?.lineItems || [];
          return (
            <>
              <div className="fw-semibold mb-1">
                Exchanges breakdown
                <i
                  className="fa-solid fa-circle-info text-muted ms-1"
                  style={{ cursor: "help" }}
                  title={exchangeTip(st.countedOperations)}
                />
              </div>
              <div className="table-responsive mb-2">
                <table className="display border-0 w-100">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Owner</th>
                      {opCols.map((o) => (
                        <th key={o} className="text-end">{opShort(o)}</th>
                      ))}
                      <th className="text-end">Exchanges</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={opCols.length + 4} className="text-muted small">
                          No exchanges in this cycle.
                        </td>
                      </tr>
                    )}
                    {items.map((li) => (
                      <tr key={String(li.keyId)}>
                        <td>{keyNameCell(li)}</td>
                        <td className="small text-muted">{li.ownerEmail || "—"}</td>
                        {opCols.map((o) => (
                          <td key={o} className="text-end">{li.byOperation?.[o] || 0}</td>
                        ))}
                        <td className="text-end">{li.count}</td>
                        <td className="text-end">{money(li.amountPence, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="fw-semibold">
                      <td colSpan={2}>Total</td>
                      {opCols.map((o) => (
                        <td key={o} className="text-end">{st.exchanges?.byOperation?.[o] || 0}</td>
                      ))}
                      <td className="text-end">{st.exchanges?.count ?? 0}</td>
                      <td className="text-end">{money(st.exchanges?.amountPence, currency)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          );
        })()
      )}

      {/* ── the other metric: tracked, not billed ── */}
      {billMetric === "key_days" && exRate && (
        <p className="text-muted small mb-3">
          Also tracked — Exchanges: {st.exchanges?.count ?? 0} (
          {money(st.exchanges?.amountPence, currency)} at the current rate), not
          billed.
        </p>
      )}
      {billMetric === "exchanges" && kdRate && (
        <p className="text-muted small mb-3">
          Also tracked — Days: {round1(st.keyDays?.total)} (
          {money(st.keyDays?.amountPence, currency)} at the current rate), not
          billed.
        </p>
      )}

      {(st.holdingNotBillable || []).length > 0 && (
        <>
          <div className="fw-semibold mb-1">
            Holding, not billable{" "}
            <span className="text-muted small">
              (key still at host but subscription lapsed — not charged)
            </span>
          </div>
          <div className="table-responsive mb-3">
            <table className="display border-0 w-100">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Owner</th>
                  <th className="text-end">Would-be days</th>
                  <th>Since</th>
                  <th>Lapsed</th>
                </tr>
              </thead>
              <tbody>
                {st.holdingNotBillable.map((h: any) => (
                  <tr key={String(h.keyId)}>
                    <td>{keyNameCell(h)}</td>
                    <td className="small text-muted">{h.ownerEmail || "—"}</td>
                    <td className="text-end">{round1(h.wouldBeDays)}</td>
                    <td>{h.from == null ? "—" : fmtDay(h.from)}</td>
                    <td>{fmtDay(h.lapsedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="text-muted small">
        Computed {fmtDateTime(st.computedAt)}
        {isFetching && " · refreshing…"}
      </div>
    </>
  );
};

// ─── cycle navigator (Statements tab body) ──────────────────────────

const CycleNavigator: React.FC<{ keyHostId: string; onGoToSettings: () => void }> = ({
  keyHostId,
  onGoToSettings,
}) => {
  const { data, isLoading } = useHostBillingCycles(keyHostId, 24);
  const cycles: any[] = data?.data?.cycles || [];
  const [idx, setIdx] = useState<number | null>(null);

  // land on the current (last) cycle whenever the host or cycle list changes
  useEffect(() => {
    if (cycles.length) setIdx(cycles.length - 1);
    else setIdx(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyHostId, cycles.length]);

  if (isLoading) return <p className="text-muted">Loading cycles…</p>;
  if (!cycles.length)
    return (
      <p className="text-muted">
        No billing cycles yet for this host. Set a billing start under{" "}
        <strong>Settings</strong> if you expected some.
      </p>
    );

  const i = idx == null ? cycles.length - 1 : idx;
  const cycle = cycles[i];

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <button
          className="btn btn-sm btn-outline-secondary"
          disabled={i <= 0}
          onClick={() => setIdx(i - 1)}
        >
          <i className="fa-solid fa-chevron-left me-1" />
          Older
        </button>
        <select
          className="form-select form-select-sm"
          style={{ width: "auto" }}
          value={i}
          onChange={(e) => setIdx(Number(e.target.value))}
        >
          {cycles.map((c, ci) => (
            <option key={c.start} value={ci}>
              {c.startLabel || fmtDay(c.start)} – {c.endLabel || fmtDay(c.end)}
              {(c.inProgress ?? Number(c.end) > nowSec()) ? "  (current)" : ""}
            </option>
          ))}
        </select>
        <button
          className="btn btn-sm btn-outline-secondary"
          disabled={i >= cycles.length - 1}
          onClick={() => setIdx(i + 1)}
        >
          Newer
          <i className="fa-solid fa-chevron-right ms-1" />
        </button>
        {i < cycles.length - 1 && (
          <button
            className="btn btn-sm btn-link"
            onClick={() => setIdx(cycles.length - 1)}
          >
            Jump to current
          </button>
        )}
      </div>

      {i === 0 && data?.data?.billingStartLabel && (
        <p className="text-muted small">
          Earliest billable cycle — billing for this host starts{" "}
          {data.data.billingStartLabel}.
        </p>
      )}

      <StatementView
        keyHostId={keyHostId}
        cycleStart={Number(cycle.start)}
        onGoToSettings={onGoToSettings}
      />
    </>
  );
};

// ─── global settings form ──────────────────────────────────────────

const GlobalSettingsForm: React.FC = () => {
  const { data, isLoading } = useHostBillingSettings();
  const update = useUpdateHostBillingSettings();
  const s = data?.data;

  const [anchor, setAnchor] = useState("1");
  const [freq, setFreq] = useState("monthly");
  const [ops, setOps] = useState<string[]>(["guest_collect", "guest_return"]);
  const [tz, setTz] = useState("Europe/London");
  const [currency, setCurrency] = useState("GBP");
  const [anchorDate, setAnchorDate] = useState("");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!s || hydrated) return;
    setAnchor(s.cycleAnchor ?? "1");
    setFreq(s.cycleFrequency ?? "monthly");
    setOps(s.countedOperations ?? ["guest_collect", "guest_return"]);
    setTz(s.timezone ?? "Europe/London");
    setCurrency(s.currency ?? "GBP");
    setAnchorDate(secToYmd(s.anchorDate));
    setHydrated(true);
  }, [s, hydrated]);

  const toggleOp = (op: string) =>
    setOps((prev) => (prev.includes(op) ? prev.filter((o) => o !== op) : [...prev, op]));

  const save = () => {
    update.mutate(
      {
        cycleAnchor: anchor,
        cycleFrequency: freq as any,
        countedOperations: ops,
        timezone: tz,
        currency,
        anchorDate: anchorDate === "" ? null : dayToCycleHint(anchorDate),
      },
      {
        onSuccess: () => okToast("Billing settings saved"),
        onError: (e: any) => errToast(e, "Could not save settings"),
      }
    );
  };

  return (
    <div className="wrapper-card mb-4">
      <h5 className="mb-1">Global billing settings</h5>
      <p className="text-muted small mb-3">
        The default cycle and counted operations for every host. A host can
        override the cycle on its own Settings tab. Changing these re-computes
        every host's past cycles.
      </p>
      {isLoading ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <div className="row g-3">
          <div className="col-sm-3">
            <label className="form-label small text-muted mb-0">
              Cycle anchor (1–28 or "last")
            </label>
            <input
              className="form-control form-control-sm"
              value={anchor}
              onChange={(e) => setAnchor(e.target.value.trim())}
            />
          </div>
          <div className="col-sm-3">
            <label className="form-label small text-muted mb-0">Cycle frequency</label>
            <select
              className="form-select form-select-sm"
              value={freq}
              onChange={(e) => setFreq(e.target.value)}
            >
              {FREQUENCIES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-3">
            <label className="form-label small text-muted mb-0">
              Anchor date (weekly/biweekly)
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={anchorDate}
              onChange={(e) => setAnchorDate(e.target.value)}
            />
          </div>
          <div className="col-sm-3">
            <label className="form-label small text-muted mb-0">Currency</label>
            <input
              className="form-control form-control-sm"
              value={currency}
              onChange={(e) => setCurrency(e.target.value.trim().toUpperCase())}
            />
          </div>
          <div className="col-sm-3">
            <label className="form-label small text-muted mb-0">Timezone</label>
            <input
              className="form-control form-control-sm"
              value={tz}
              onChange={(e) => setTz(e.target.value.trim())}
            />
          </div>
          <div className="col-sm-9">
            <label className="form-label small text-muted mb-0 d-block">
              Counted exchange operations
            </label>
            {COUNTABLE_OPS.map((op) => (
              <div className="form-check form-check-inline" key={op}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`op-${op}`}
                  checked={ops.includes(op)}
                  onChange={() => toggleOp(op)}
                />
                <label className="form-check-label small" htmlFor={`op-${op}`}>
                  {humanizeOp(op)}
                </label>
              </div>
            ))}
          </div>
          <div className="col-12">
            <button
              className="btn btn-sm btn-primary"
              disabled={update.isLoading}
              onClick={save}
            >
              Save settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── one host's rate editors + cycle override (Settings tab body) ───

const HostConfig: React.FC<{ keyHostId: string }> = ({ keyHostId }) => {
  const rates = useHostBillingRates(keyHostId);
  const overrides = useHostBillingOverrides();
  const setMetric = useUpsertHostBillingOverride();

  const rateRows: any[] = rates.data?.data || [];
  const keyDayRates = rateRows.filter((r) => r.dimension === "key_days");
  const exchangeRates = rateRows.filter((r) => r.dimension === "exchanges");
  const override = (overrides.data?.data || []).find(
    (o: any) => String(o.keyHostId) === String(keyHostId)
  );
  const kdRate = keyDayRates.length > 0;
  const exRate = exchangeRates.length > 0;
  const chosen: string | null = override?.billingMetric ?? null;
  const effective = kdRate && exRate ? chosen : kdRate ? "key_days" : exRate ? "exchanges" : null;

  const pick = (m: "key_days" | "exchanges") =>
    setMetric.mutate(
      { keyHostId, billingMetric: m },
      {
        onSuccess: () => okToast(`Billing this host on ${METRIC_LABEL[m]}`),
        onError: (e: any) => errToast(e, "Could not set the billing metric"),
      }
    );

  return (
    <>
      <h6 className="mb-1">Billing metric</h6>
      <p className="text-muted small">
        A host is billed on <strong>one</strong> metric — days or exchanges,
        never both.
      </p>
      {!kdRate && !exRate ? (
        <p className="text-muted small">
          No rate configured yet — add one below and it becomes the billing
          metric automatically.
        </p>
      ) : kdRate && exRate ? (
        <div className="mb-2">
          {["key_days", "exchanges"].map((m) => (
            <div className="form-check" key={m}>
              <input
                className="form-check-input"
                type="radio"
                name="billingMetric"
                id={`bm-${m}`}
                checked={chosen === m}
                disabled={setMetric.isLoading}
                onChange={() => pick(m as "key_days" | "exchanges")}
              />
              <label className="form-check-label" htmlFor={`bm-${m}`}>
                {METRIC_LABEL[m]}
              </label>
            </div>
          ))}
          {!chosen && (
            <div className="text-warning small mt-1">
              Rates exist for both — pick one or the statement can't be billed.
            </div>
          )}
        </div>
      ) : (
        <p className="small">
          Billing on <strong>{METRIC_LABEL[effective || ""]}</strong>{" "}
          <span className="text-muted">(only rate configured)</span>
        </p>
      )}

      <hr />
      <h6 className="mb-1">Rates</h6>
      <p className="text-muted small">
        Rates are temporal — the rate in force on each day of a cycle is the one
        used. Editing a rate re-computes every past cycle it touches.
      </p>
      <RateEditor keyHostId={keyHostId} dimension="key_days" rows={keyDayRates} />
      <RateEditor keyHostId={keyHostId} dimension="exchanges" rows={exchangeRates} />

      <hr />
      <h6 className="mb-1">Cycle override</h6>
      <p className="text-muted small">
        Leave blank to inherit the global cycle. A billing-start date stops any
        charge before it.
      </p>
      <OverrideEditor keyHostId={keyHostId} override={override} />
    </>
  );
};

// ─── per-host detail view (Statements / Settings) ──────────────────

const HostBillingDetail: React.FC<{
  host: { id: string; name: string };
  onBack: () => void;
}> = ({ host, onBack }) => {
  const [tab, setTab] = useState<"statements" | "settings">("statements");

  return (
    <>
      <div className="wrapper-card mb-3">
        <button className="btn btn-sm btn-link p-0 text-decoration-none" onClick={onBack}>
          <i className="fa-solid fa-chevron-left me-1" />
          All hosts
        </button>
        <h4 className="mb-0 mt-1">{host.name || "Key host"}</h4>
      </div>

      <div className="wrapper-card mb-4">
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              className={`nav-link${tab === "statements" ? " active" : ""}`}
              onClick={() => setTab("statements")}
            >
              Statements
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link${tab === "settings" ? " active" : ""}`}
              onClick={() => setTab("settings")}
            >
              Settings
            </button>
          </li>
        </ul>

        {tab === "statements" && (
          <CycleNavigator keyHostId={host.id} onGoToSettings={() => setTab("settings")} />
        )}
        {tab === "settings" && <HostConfig keyHostId={host.id} />}
      </div>
    </>
  );
};

// ─── host list (landing) ──────────────────────────────────────────

const HostList: React.FC<{ onOpen: (h: { id: string; name: string }) => void }> = ({
  onOpen,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("1");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = useGetKeyHost({
    page: String(page),
    limit: String(limit),
    search,
    status,
  });
  const hosts: any[] = data?.data?.keyHosts || [];
  const totalPages = data?.data?.totalPage || 1;
  const totalRecord = data?.data?.totalRecord;

  const [runDay, setRunDay] = useState(todayYmd());
  const [csvBusy, setCsvBusy] = useState(false);
  const downloadRun = () => {
    setCsvBusy(true);
    exportBillingSummaryCsv(dayToCycleHint(runDay))
      .catch((e) => toast.error(apiErr(e, "CSV export failed")))
      .finally(() => setCsvBusy(false));
  };

  return (
    <>
      <div className="wrapper-card mb-4">
        <div className="d-flex flex-wrap gap-2 align-items-end justify-content-between">
          <div className="d-flex gap-2 align-items-end flex-wrap">
            <div>
              <label className="form-label small text-muted mb-0">Search</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Store name…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div>
              <label className="form-label small text-muted mb-0">Status</label>
              <select
                className="form-select form-select-sm"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="1">Active</option>
                <option value="0">Disabled</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
          <div className="d-flex gap-2 align-items-end">
            <div>
              <label className="form-label small text-muted mb-0">
                Billing run — all hosts, one cycle
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={runDay}
                onChange={(e) => setRunDay(e.target.value)}
              />
            </div>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={csvBusy}
              onClick={downloadRun}
            >
              <i className="fa-solid fa-download me-1" />
              CSV
            </button>
          </div>
        </div>
      </div>

      <div className="wrapper-card mb-4">
        <div className="table-blk">
          <div className="table-responsive">
            <table className="display border-0 w-100">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {hosts.map((h) => (
                  <tr
                    key={h._id}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      onOpen({
                        id: String(h._id),
                        name: h.storeName || h.store_name || "",
                      })
                    }
                  >
                    <td className="fw-semibold">
                      {h.storeName || h.store_name || "—"}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          h.status === 1 ? "bg-success" : "bg-secondary"
                        }`}
                      >
                        {h.status === 1 ? "active" : "inactive"}
                      </span>
                    </td>
                    <td className="text-end text-muted">
                      <i className="fa-solid fa-chevron-right" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isLoading && <p className="text-muted p-3">Loading hosts…</p>}
          {!isLoading && hosts.length === 0 && (
            <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
              <i className="fa-solid fa-store fa-2x mb-2" />
              <h5 className="mb-1">No hosts match</h5>
            </div>
          )}
        </div>

        {totalRecord !== undefined && (
          <div className="text-muted small mt-2">{totalRecord} host(s)</div>
        )}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          handlePrevPage={() => setPage((p) => Math.max(1, p - 1))}
          handleNextPage={() => setPage((p) => p + 1)}
          handleRowLimitChange={(v: number) => {
            setLimit(v);
            setPage(1);
          }}
        />
      </div>
    </>
  );
};

// ─── screen ───────────────────────────────────────────────────────

const HostBillingScreen: React.FC = () => {
  const [tab, setTab] = useState<"hosts" | "global">("hosts");
  const [host, setHost] = useState<{ id: string; name: string } | null>(null);

  return (
    <div className="content-wrapper">
      <Header title="Host Billing" />
      <div className="main-wrapper px-4 pt-4 pb-4">
        {host ? (
          <HostBillingDetail host={host} onBack={() => setHost(null)} />
        ) : (
          <>
            <ul className="nav nav-tabs mb-4">
              <li className="nav-item">
                <button
                  className={`nav-link${tab === "hosts" ? " active" : ""}`}
                  onClick={() => setTab("hosts")}
                >
                  Hosts
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link${tab === "global" ? " active" : ""}`}
                  onClick={() => setTab("global")}
                >
                  Global settings
                </button>
              </li>
            </ul>

            {tab === "hosts" && <HostList onOpen={setHost} />}
            {tab === "global" && <GlobalSettingsForm />}
          </>
        )}
      </div>
    </div>
  );
};

export default HostBillingScreen;
